import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { SlotClient, SlotWorkflowClient } from '@frontend/slot/api';
import { Slot, SlotRendering } from '@frontend/slot/types';
import { TransactionClient } from '@frontend/transaction/api';
import { Transaction, TransactionStateName } from '@frontend/transaction/types';
import React, { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { SlotLayoutCell, slotLayoutDisplayNumber } from '../../components/slot-layout-cell';
import { isMovableTransactionState } from '../../hooks/useMoveTransaction';
import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { useLicenseRepository } from '../../utils/license-repository';
import { isMoveDestinationSlotSelectable } from './move-destination-slot-selectable';

const REQUIRED_AVAILABLE_SLOT_COUNT = 1;

export interface MoveSelectSlotViewProps {
    accountId: string | undefined;
    spotId: string | undefined;
    currentModule: Module | null;
    modules: Module[] | null;
    selectedSlot: Slot | null;
    setSelectedSlot: (slot: Slot | null) => void;
    setCurrentModule: (module: Module | null) => void;
    slotComponent: (slotRendering: SlotRendering) => React.ReactNode;
    selectModuleModalOpen: boolean;
    setSelectModuleModalOpen: (open: boolean) => void;
    isLoading: boolean;
    error: ReactNode | null;
    canContinue: boolean;
    handleContinue: () => void;
    handleBack: () => void;
}

export const useMoveSelectSlot = (): MoveSelectSlotViewProps => {
    const {
        slotId: sourceSlotId,
        transactionId,
        destSpotId
    } = useParams<{
        slotId: string;
        transactionId: string;
        destSpotId: string;
    }>();
    const navigate = useKioskNavigate();
    const navigationState = useAppSelector((state) => state.navigation);
    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const { getFunctionality } = useLicenseRepository();
    const moveFunctionality = getFunctionality(Application.PUDO, Feature.MOVE_TRANSACTION);

    const [modules, setModules] = useState<Module[] | null>(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    const [allSlots, setAllSlots] = useState<Slot[] | null>(null);
    const [availableSlotIds, setAvailableSlotIds] = useState<Set<string>>(new Set());
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
    const [selectModuleModalOpen, setSelectModuleModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<ReactNode | null>(null);
    const [transaction, setTransaction] = useState<Transaction | null>(null);

    useEffect(() => {
        if (!destSpotId || !transactionId || !accountId) {
            setIsLoading(false);
            setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            return;
        }

        setIsLoading(true);
        setError(null);
        setSelectedSlot(null);

        Promise.all([
            TransactionClient.fetchTransaction(accountId, transactionId),
            ModuleClient.fetchModules({ spot_id: destSpotId }),
            SlotClient.fetchSlots({ spot_id: destSpotId, size: '1000', index: '0' }),
            SlotWorkflowClient.postAvailableSlots(accountId, destSpotId, {
                account_id: accountId,
                application: Application.PUDO,
                feature: Feature.MOVE_TRANSACTION,
                functionality_id: moveFunctionality?.id,
                transaction_count: REQUIRED_AVAILABLE_SLOT_COUNT
            })
        ])
            .then(([trx, modulesRes, slotsRes, availableRes]) => {
                if (!isMovableTransactionState(trx.state as TransactionStateName)) {
                    setError(CommonMessage.ERRORS.DEFAULT);
                    setTransaction(null);
                    return;
                }
                setTransaction(trx);
                const sorted = modulesRes.results.sort((a, b) => {
                    const indexA = a.index || '';
                    const indexB = b.index || '';
                    return indexA.localeCompare(indexB, undefined, { numeric: true, sensitivity: 'base' });
                });
                setModules(sorted);
                setAllSlots(slotsRes.results);
                setCurrentModuleIndex(0);
                setAvailableSlotIds(new Set((availableRes.results ?? []).map((slot) => slot.id)));
            })
            .catch((err) => {
                Logger.error('Failed to load move transaction slot selection', err);
                setAvailableSlotIds(new Set());
                setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            })
            .finally(() => setIsLoading(false));
    }, [destSpotId, transactionId, accountId, moveFunctionality?.id]);

    const currentModule = modules && modules.length > 0 ? modules[currentModuleIndex] : null;

    const setCurrentModule = (module: Module | null) => {
        if (modules && module) {
            const index = modules.findIndex((m) => m.id === module.id);
            if (index >= 0) setCurrentModuleIndex(index);
        }
        setSelectModuleModalOpen(false);
    };

    const transactionSpotId = transaction?.spot_id ?? navigationState.spot?.id;

    const handleSlotClick = useCallback(
        (slot: Slot) => {
            if (!availableSlotIds.has(slot.id)) return;
            if (slot.id === sourceSlotId && destSpotId === transactionSpotId) {
                return;
            }
            setSelectedSlot(slot);
        },
        [availableSlotIds, sourceSlotId, destSpotId, transactionSpotId]
    );

    const slotComponent = useCallback(
        (slotRendering: SlotRendering): React.ReactNode => {
            const renderedSlotId = slotRendering.slot_ids.find((id) => allSlots?.some((s) => s.id === id));
            const slot = allSlots?.find((s) => s.id === renderedSlotId) ?? null;
            const isCurrentSource = !!slot && slot.id === sourceSlotId && destSpotId === transactionSpotId;
            const isSelected = selectedSlot != null && slotRendering.slot_ids.includes(selectedSlot.id);
            const slotNumber = slotLayoutDisplayNumber(slot);
            const hasTransaction = (slotRendering.transactions?.length ?? 0) > 0;
            const hasContent = slotRendering.occupied === true || (slotRendering.stocks?.length ?? 0) > 0 || hasTransaction;
            const isAvailable = !!slot && availableSlotIds.has(slot.id);
            const isSelectable = isMoveDestinationSlotSelectable({ slot, isCurrentSource, isAvailable });

            return (
                <SlotLayoutCell
                    slotNumber={slotNumber}
                    isSelected={isSelected}
                    isDisabled={!isSelectable}
                    hasContent={hasContent}
                    onSelect={isSelectable && slot ? () => handleSlotClick(slot) : undefined}
                />
            );
        },
        [allSlots, availableSlotIds, selectedSlot, handleSlotClick, sourceSlotId, destSpotId, transactionSpotId]
    );

    const handleContinue = useCallback(() => {
        if (!sourceSlotId || !transactionId || !destSpotId || !selectedSlot) return;
        if (!availableSlotIds.has(selectedSlot.id)) return;
        navigate(`/spots/layout/slot/${sourceSlotId}/move/${transactionId}/spot/${destSpotId}/slot/${selectedSlot.id}/confirm`);
    }, [navigate, sourceSlotId, transactionId, destSpotId, selectedSlot, availableSlotIds]);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const hasEnoughAvailableSlots = availableSlotIds.size >= REQUIRED_AVAILABLE_SLOT_COUNT;

    return {
        accountId,
        spotId: destSpotId,
        currentModule,
        modules,
        selectedSlot,
        setSelectedSlot,
        setCurrentModule,
        slotComponent,
        selectModuleModalOpen,
        setSelectModuleModalOpen,
        isLoading,
        error,
        canContinue: !!selectedSlot && !!transaction && !error && hasEnoughAvailableSlots && availableSlotIds.has(selectedSlot.id),
        handleContinue,
        handleBack
    };
};
